import React, { useEffect, useRef, useState } from 'react';
import { useAsync } from 'react-use';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-zero/react';
import { combineActions } from 'redux-zero/utils';
import { formActions, holderActions } from 'app/state/actions';
import useWeb3ModalWithCallback from 'app/helpers/useWeb3ModalWithCallback';
import {
  DsButton,
  DsInputBignumber,
  DsForm,
  DsIcon,
  DsExplorerLink,
  DsLoader,
} from '@tokenyico/design-system/dist/reactJS';
import {
  dsConfirm,
  vBigNumber,
  vMaxBigNumber,
  vGreaterThanBigNumber,
} from '@tokenyico/design-system';
import { TokenService, HolderService, DFNSService } from 'services';
import { getApproveDirectContent } from 'app/views/Platform/views/Dashboard/Confirmation';

import './CentralizedExchange.less';

const SendTokensDialog = ({
  cex,
  onCloseModal,
  setSuccess,
  setBalances,
  holder: { selectedToken },
}) => {
  const network = selectedToken.blockchainNetwork;
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [balance, setBalance] = useState(null);

  const [errorSubmit, setErrorSubmit] = useState();
  const [errorWeb3, setErrorWeb3] = useState();

  const [tokenQuantity, setTokenQuantity] = useState('');
  const tokenQuantityRef = useRef(null);

  const { callContract, onConnectWallet, isWeb3ModalOpen } = useWeb3ModalWithCallback(async () => {
    if (isValid && tokenQuantity) onSubmit();
  });

  const request = useAsync(async () => {
    try {
      const newbalances = await HolderService.getBalances(selectedToken.id);
      setBalance(newbalances[0]);
      setBalances(newbalances);
    } catch (e) {
      setError(e);
    }
  });

  const handleCloseModal = () => {
    setIsSubmitting(false);
    setTokenQuantity(null);
    setErrorSubmit(null);
    setErrorWeb3(null);
    onCloseModal();
  };

  //reset form on close
  useEffect(() => {
    if (!cex) {
      setTokenQuantity('');
      tokenQuantityRef.current?.hideError();
      setErrorSubmit(null);
      setErrorWeb3(null);
    }
  }, [onCloseModal]);

  const onSubmit = async () => {
    const symbol = get(balance, 'token.symbol');
    const tokenDecimals = get(balance, 'token.decimals', 0);
    const sender = get(balance, 'holder.details.wallet');
    const tokenAddress = get(balance, 'token.address');
    const provider = get(balance, 'holder.details.provider');
    const tokenId = get(balance, 'token.id');
    const recipient = cex?.depositWalletAddress;

    if (
      isValid &&
      (await dsConfirm({
        heading: `${t('shared.transferTokens')}?`,
        content: getApproveDirectContent(t, {
          tokenQuantity,
          symbol,
          sender,
          recipient,
          investor: '',
          token: selectedToken,
        }),
        action: `${t('shared.yes')}, ${t('shared.transferTokens').toLocaleLowerCase()}`,
        version: 'success',
      }))
    ) {
      setErrorSubmit(null);
      setErrorWeb3(null);
      setIsSubmitting(true);

      const data = {
        tokenQuantity: tokenQuantity,
        ethereumNetwork: network.toUpperCase(),
        tokenAddress,
        senderWallet: sender,
        recipientWallet: recipient,
      };

      if (provider === 'INTEGRATED') {
        try {
          await DFNSService.transfer(selectedToken, data);
          setSuccess(t('shared.transactionBroadcasted'));
          handleCloseModal();
        } catch (e) {
          setIsSubmitting(false);
          setErrorSubmit(e.message);
        }
      } else {
        try {
          await TokenService.postValidateTransfer(tokenId, data);
          if (provider === 'ONCHAINID') {
            try {
              await TokenService.postInitSimpleTransfer(tokenId, omit(data, 'tokenAddress'));
            } catch (e) {
              catchWeb3Error(e);
            } finally {
              setSuccess(t('shared.transactionBroadcasted'));
              handleCloseModal();
            }
          } else {
            // if wallet provider != onchainid
            const isWalletConnected = onConnectWallet();
            if (!isWalletConnected) return;
            return handleTransferToken({
              tokenAddress: data.tokenAddress,
              fromAddress: data.senderWallet,
              toAddress: data.recipientWallet,
              amount: data.tokenQuantity,
              decimals: tokenDecimals,
            }).catch((res) => {
              catchWeb3Error(res);
            });
          }
        } catch (e) {
          setIsSubmitting(false);
          setErrorSubmit(e.message);
        }
      }
    }
  };

  const handleTransferToken = async (data) => {
    return callContract('transfer', data).then(() => {
      setSuccess(t('shared.transactionBroadcasted'));
      handleCloseModal();
    });
  };

  const catchWeb3Error = (res) => {
    const error = res.details || res.message; // res.messages is too long to display.
    setErrorWeb3(error);
    setIsSubmitting(false);
  };

  return (
    <DsForm className="send-token" onSubmit={onSubmit} onValid={({ detail }) => setIsValid(detail)}>
      {request.loading ? (
        <div className="_p(4) _flex:center">
          <DsLoader version="dots" color="var(--blue-500)" />
        </div>
      ) : (
        <>
          {!balance ? (
            <div className="_p(4) _align:center" style={{ color: 'red' }}>
              {t('shared.errorInsufficientBalance')}
            </div>
          ) : (
            <div className="send-token__card">
              <h3 className="send-token__card__title">{t('shared.sendTokensToDepositWallet')}</h3>

              <div className="send-token__card__info dialog-card">
                <div className="send-token__card__info--link">
                  <DsExplorerLink
                    className="_m:top(2)"
                    address={balance.holder.wallet}
                    network={selectedToken.blockchainNetwork.toLowerCase()}
                    type="wallet"
                    width="175"
                    isCopyable
                  />
                </div>

                <div className="send-token__card__info--balance ">
                  {t('shared.balance')}:{' '}
                  <span>
                    {balance.unfrozenBalance} {selectedToken.symbol}
                  </span>
                </div>
              </div>

              <div className="send-token__card__amount dialog-card">
                <div className="send-token__card__amount--label">
                  <DsIcon size="12px" name="sending" className="_p:right(2)" />
                  {t('shared.amountToSend')} *
                </div>

                <div className="send-token__card__amount--input">
                  <div>
                    <DsInputBignumber
                      isFloatLabel={true}
                      name="tokenQuantity"
                      type="text"
                      value={tokenQuantity}
                      placeholder="0.00"
                      onDsInput={({ detail }) => setTokenQuantity(detail)}
                      validate={(v) =>
                        vBigNumber(selectedToken.decimals)(v) ||
                        vGreaterThanBigNumber(0)(v) ||
                        vMaxBigNumber(balance.unfrozenBalance)(v)
                      }
                      ref={tokenQuantityRef}
                      isFullWidth
                    />
                  </div>

                  <div className="send-token__card__amount--token">
                    <img height="18" src={selectedToken.image} className="_m:right(2)" />
                    <span>{selectedToken.symbol}</span>
                  </div>
                </div>
              </div>

              <div className="_mtop-4">
                <div className="_text:md _text-coolGrey-900">
                  {t('shared.depositWalletAddress')} *
                </div>
                <div>
                  <DsExplorerLink
                    className="_mtop-3"
                    address={cex?.depositWalletAddress}
                    network={selectedToken.blockchainNetwork.toLowerCase()}
                    type="wallet"
                    isCopyable
                  />
                </div>
              </div>

              <div className="_m:x(auto) _mtop-2">
                {errorSubmit && <div className="help-error">{errorSubmit}</div>}
                {errorWeb3 && <div className="help-error">{errorWeb3}</div>}
              </div>

              <div className="send-token__card__cta">
                <DsButton
                  mode="text"
                  label={t('shared.cancel')}
                  isReverse
                  type="button"
                  className="_m:left(2)"
                  onClick={() => handleCloseModal()}
                />

                <DsButton
                  version="primary"
                  label={t('shared.transferTokens')}
                  icon="checkmark"
                  isReverse
                  type="submit"
                  isLoading={isSubmitting || isWeb3ModalOpen}
                  className="_m:left(2)"
                  onClick={onSubmit}
                />
              </div>
            </div>
          )}
        </>
      )}
    </DsForm>
  );
};

const mapToProps = (state) => state;

export default connect(mapToProps, combineActions(formActions, holderActions))(SendTokensDialog);
